import { useState } from 'react';
import moment from 'moment';
import { DateType } from 'ui/datePicker';
import { getParam } from 'utils/urlParams';
import { fourteenDaysAgo, yesterday, convertDateToPST } from '../utils';

export const queryParamKeys = {
  endDate: 'endDate',
  startDate: 'startDate',
};

export const initStartDate = () => {
  const param = getParam(window.location.href, queryParamKeys.startDate);
  return param ? moment(param) : fourteenDaysAgo();
};

export const initEndDate = () => {
  const param = getParam(window.location.href, queryParamKeys.endDate);
  return param ? moment(param) : yesterday();
};

export const syncQueryParams = (start: DateType, end: DateType) => {
  const startDateString = start?.format('YYYY-MM-DD') as string;
  const endDateString = end?.format('YYYY-MM-DD') as string;
  const queryParams = new URLSearchParams(window.location.search);
  queryParams.set('startDate', startDateString);
  queryParams.set('endDate', endDateString);
  window.history.replaceState(null, '', `${window.location.pathname}?${queryParams.toString()}`);
};

export const useDateSelection = () => {
  /**
   * If the user accesses this page with query params already set, we honor them by
   * initializing state with those values. Earlier we had been using useSearchParamState,
   * but we only wanted to store a string for date, not datetime.
   */
  const [startDate, setStartDate] = useState<DateType>(initStartDate);
  const [endDate, setEndDate] = useState<DateType>(initEndDate);

  const setDateRange = ({
    startDate: start,
    endDate: end,
  }: {
    startDate: DateType;
    endDate: DateType;
  }) => {
    setStartDate(start);
    setEndDate(end);

    /**
     * We explicitly sync the query params to the state, in case the user refreshes or
     * changes the params then shares the url with someone else.
     */
    syncQueryParams(start, end);
  };

  /**
   * The time in this datetime is not used -- on the BE it queries against
   * aggregated data in Snowflake which is only collected once per day.
   * The time is only present as an artifact of when the BE endpoint queried
   * Elasticsearch directly. Eventually we will remove the time from these
   * parameters altogether, but until then we will simply hard-code a set
   * time of midnight at PST.
   */
  const startDateTimePST = convertDateToPST(startDate);
  const endDateTimePST = convertDateToPST(endDate);

  const dateRange = { startDate, endDate };

  return {
    setDateRange,
    dateRange,
    startDateTimePST,
    endDateTimePST,
  };
};
